/**
 * 학부모 상담 기록 조회 페이지
 * - 자녀별 상담 기록 목록
 * - 상담 상세 보기
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Chip,
  Tabs,
  Tab,
  Divider,
  Collapse,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ChatIcon from '@mui/icons-material/Chat';
import { getAccessToken } from '../../../lib/authUtils';
import { API_BASE_URL } from '../../../config/api';

interface CounselingRecord {
  id: string;
  student_id: string;
  counselor_name: string;
  counseling_date: string;
  counseling_type: string;
  duration_minutes: number | null;
  topic: string;
  content: string;
  student_feedback: string | null;
  growth_score: number | null;
  attitude_score: number | null;
  concentration_score: number | null;
  goal_achievement_score: number | null;
  current_issues: string | null;
  action_plan: string | null;
  next_goal: string | null;
  next_counseling_date: string | null;
  created_at: string;
}

interface ChildCounseling {
  student_id: string;
  student_name: string;
  total_count: number;
  records: CounselingRecord[];
}

interface CounselingData {
  children: ChildCounseling[];
  total_count: number;
}

const counselingTypeLabels: Record<string, string> = {
  regular: '정기 상담',
  emergency: '긴급 상담',
  parent_request: '학부모 요청',
  special: '특별 상담',
};

function ScoreBadge({ label, score }: { label: string; score: number | null }) {
  if (score == null) return null;
  const color = score >= 4 ? 'success' : score >= 3 ? 'warning' : 'error';
  return (
    <Chip
      label={`${label} ${score}/5`}
      size="small"
      color={color}
      variant="outlined"
      sx={{ mr: 0.5, mb: 0.5 }}
    />
  );
}

function RecordCard({ record }: { record: CounselingRecord }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <Card sx={{ mb: 2 }} variant="outlined">
      <CardContent sx={{ pb: expanded ? 2 : '12px !important' }}>
        {/* 헤더 */}
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box flex={1}>
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
              <Typography variant="subtitle1" fontWeight={600}>
                {record.topic}
              </Typography>
              <Chip
                label={counselingTypeLabels[record.counseling_type] || record.counseling_type}
                size="small"
                color="primary"
                variant="outlined"
              />
            </Box>
            <Typography variant="body2" color="text.secondary">
              {record.counseling_date} | {record.counselor_name}
              {record.duration_minutes ? ` | ${record.duration_minutes}분` : ''}
            </Typography>
          </Box>
          <IconButton onClick={() => setExpanded(!expanded)} size="small">
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        {/* 평가 점수 (항상 표시) */}
        {(record.growth_score || record.attitude_score || record.concentration_score || record.goal_achievement_score) && (
          <Box mt={1}>
            <ScoreBadge label="성장" score={record.growth_score} />
            <ScoreBadge label="태도" score={record.attitude_score} />
            <ScoreBadge label="집중" score={record.concentration_score} />
            <ScoreBadge label="목표" score={record.goal_achievement_score} />
          </Box>
        )}

        {/* 상세 내용 (토글) */}
        <Collapse in={expanded}>
          <Divider sx={{ my: 1.5 }} />

          <Box mb={1.5}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              상담 내용
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {record.content}
            </Typography>
          </Box>

          {record.student_feedback && (
            <Box mb={1.5}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                학생 피드백
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {record.student_feedback}
              </Typography>
            </Box>
          )}

          {record.current_issues && (
            <Box mb={1.5}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                현재 문제점
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {record.current_issues}
              </Typography>
            </Box>
          )}

          {record.action_plan && (
            <Box mb={1.5}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                실행 계획
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {record.action_plan}
              </Typography>
            </Box>
          )}

          {record.next_goal && (
            <Box mb={1.5}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                다음 목표
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {record.next_goal}
              </Typography>
            </Box>
          )}

          {record.next_counseling_date && (
            <Box>
              <Typography variant="body2" color="text.secondary">
                다음 상담 예정일: {record.next_counseling_date}
              </Typography>
            </Box>
          )}
        </Collapse>
      </CardContent>
    </Card>
  );
}

export default function CounselingPage() {
  const [data, setData] = useState<CounselingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState(0);

  useEffect(() => {
    loadCounselingRecords();
  }, []);

  const loadCounselingRecords = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getAccessToken();
      if (!token) {
        setError('로그인이 필요합니다.');
        return;
      }

      const res = await fetch(`${API_BASE_URL}/v1/parent/counseling-records`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => null);
        throw new Error(errData?.detail || '상담 기록을 불러올 수 없습니다.');
      }

      const result = await res.json();
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : '오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!data || data.children.length === 0) {
    return (
      <Box p={2}>
        <Typography variant="h6" gutterBottom fontWeight={600}>
          상담 기록
        </Typography>
        <Alert severity="info">등록된 상담 기록이 없습니다.</Alert>
      </Box>
    );
  }

  const currentChild = data.children[selectedTab] || data.children[0];

  return (
    <Box p={2}>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <ChatIcon color="primary" />
        <Typography variant="h6" fontWeight={600}>
          상담 기록
        </Typography>
        <Chip label={`총 ${data.total_count}건`} size="small" color="primary" />
      </Box>

      {/* 자녀 탭 (2명 이상일 때만) */}
      {data.children.length > 1 && (
        <Tabs
          value={selectedTab}
          onChange={(_, v) => setSelectedTab(v)}
          sx={{ mb: 2 }}
          variant="scrollable"
          scrollButtons="auto"
        >
          {data.children.map((child, idx) => (
            <Tab
              key={child.student_id}
              label={`${child.student_name} (${child.total_count}건)`}
            />
          ))}
        </Tabs>
      )}

      {/* 자녀 이름 (1명일 때) */}
      {data.children.length === 1 && (
        <Typography variant="subtitle1" color="text.secondary" mb={2}>
          {currentChild.student_name}의 상담 기록 ({currentChild.total_count}건)
        </Typography>
      )}

      {/* 상담 기록 목록 */}
      {currentChild.records.length === 0 ? (
        <Alert severity="info">{currentChild.student_name}의 상담 기록이 없습니다.</Alert>
      ) : (
        currentChild.records.map((record) => (
          <RecordCard key={record.id} record={record} />
        ))
      )}
    </Box>
  );
}
